import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  CircularProgress,
  Chip,
  Stack,
  Paper,
  Breadcrumbs,
  Link,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DescriptionIcon from "@mui/icons-material/Description";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import LibraryBooksOutlinedIcon from "@mui/icons-material/LibraryBooksOutlined";
import FolderIcon from "@mui/icons-material/Folder";
import HomeIcon from "@mui/icons-material/Home";
import CreateNewFolderIcon from "@mui/icons-material/CreateNewFolder";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import { Menu, MenuItem, ListItemIcon, ListItemText } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { get_brand_library, download_file, get_sidebar_menu, remove_brand, get_folders, create_folder, update_folder, delete_folder } from "../../store/job/jobSlice";


import { get_company_process, setStageTab } from "../../store/user/userSlice";
import { useNavigate } from "react-router-dom";
import PdfThumbnail from "../../components/customer_portal/job/PdfThumbnail";
import { SvgIcon } from "@mui/material";

const DeleteSvgIcon = (props) => (
  <SvgIcon {...props}>
    <path
      d="M3 6h18m-2 0v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6m3 0V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2m-6 5v6m4-6v6"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </SvgIcon>
);

const IMAGE_REGEX = new RegExp("\\.(jpeg|jpg|gif|png|webp)($|\\?)", "i");
const PDF_REGEX = new RegExp("\\.pdf($|\\?)", "i");

// function replacement
const formatDateTime = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  const rawHours = date.getHours();
  const ampm = rawHours >= 12 ? "PM" : "AM";
  const hours12 = rawHours % 12 || 12;                        // 0 → 12
  const hours = String(hours12).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  return `${day}-${month}-${year} ${hours}:${minutes} ${ampm}`;
};



const getFullUrl = (path) => {
  if (!path || typeof path !== "string") return null;
  if (path.startsWith("http://") || path.startsWith("https://")) return path;

  const LINODE_BUCKET = "sewrobj";
  const LINODE_REGION = "us-east-1";
  return encodeURI(
    `https://${LINODE_BUCKET}.${LINODE_REGION}.linodeobjects.com/${path.replace(/^\/+/, "")}`
  );
};

const isImage = (url) => !!url && IMAGE_REGEX.test(url.split("?")[0]);
const isPdf = (url) => !!url && PDF_REGEX.test(url.split("?")[0]);

const getExtension = (path) => {
  if (!path) return "";
  return "." + path.split(".").pop().toUpperCase();
};

const getFileName = (path) => {
  if (!path) return "";
  const base = path.split("/").pop().split("?")[0];
  try {
    return decodeURIComponent(base);
  } catch {
    return base;
  }
};

const BrandLibraryPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { branded_library, loading, sidebar_menus } = useSelector((state) => state.job);
  const { selected_company, company_process, selected_stage_tab, selected_process_tab, companies } =
    useSelector((state) => state.user);

  const [search, setSearch] = useState("");
  const [brokenImages, setBrokenImages] = useState({});
  const [page, setPage] = useState(1);
  const ITEMS_PER_PAGE = 8;

  // Folder states
  const { folders, folderLoading } = useSelector((state) => state.job);
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [breadcrumbs, setBreadcrumbs] = useState([{ id: null, name: "Home" }]);
  const [isCreateFolderOpen, setIsCreateFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [createFolderError, setCreateFolderError] = useState("");

  // Folder Menu / Rename / Delete States
  const [folderMenuAnchor, setFolderMenuAnchor] = useState(null);
  const [activeFolder, setActiveFolder] = useState(null);
  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [renameFolderError, setRenameFolderError] = useState("");
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [folderNotEmptyError, setFolderNotEmptyError] = useState(""); // non-empty folder error msg

  // File delete state
  const [fileDeleteTarget, setFileDeleteTarget] = useState(null);
  const [isFileDeleteConfirmOpen, setIsFileDeleteConfirmOpen] = useState(false);


  const companyLabel = useMemo(() => {
    if (!companies || !selected_company) return null;
    return companies.find(c => c.id === selected_company)?.company_name;
  }, [companies, selected_company]);

  // ✅ single source of truth: stage id decides personal/brand
  const isPersonal = useMemo(() => {
    const stageId = String(selected_stage_tab || "").toLowerCase().trim();
    return stageId === "my_library";
  }, [selected_stage_tab]);

  // Pagination
  const pageCount = Math.ceil((branded_library?.length || 0) / ITEMS_PER_PAGE);
  const paginatedItems =
    branded_library?.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE) || [];

  // ✅ fetch sidebar menu when process changes (if you need it here)
  useEffect(() => {
    if (selected_process_tab) {
      dispatch(get_sidebar_menu(selected_process_tab));
    }
  }, [dispatch, selected_process_tab]);

  // ✅ ensure company_process loaded
  useEffect(() => {
    if (selected_company && (!company_process || company_process.length === 0)) {
      dispatch(get_company_process(selected_company));
    }
  }, [dispatch, selected_company, company_process]);

  // ✅ MAIN LIST FETCH: runs when company / stage / folder changes
  useEffect(() => {
    if (!selected_company) return;

    setPage(1); // reset to first page when switching library type
    dispatch(
      get_brand_library({
        search: search.trim(),
        company_id: selected_company,
        is_personal: isPersonal,
        folder_id: currentFolderId
      })
    );
    dispatch(
      get_folders({
        parent_id: currentFolderId,
        is_personal: isPersonal,
        search: search.trim()
      })
    );
  }, [dispatch, selected_company, isPersonal, currentFolderId]);


  const handleSearch = () => {
    if (!selected_company) return;
    setPage(1);
    dispatch(
      get_brand_library({
        search: search.trim(),
        company_id: selected_company,
        is_personal: isPersonal,
      })
    );
    dispatch(
      get_folders({
        parent_id: currentFolderId,
        is_personal: isPersonal,
        search: search.trim()
      })
    );
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  const handleFolderClick = (folder) => {
    setCurrentFolderId(folder.id);
    setBreadcrumbs((prev) => [...prev, { id: folder.id, name: folder.name }]);
  };

  const handleBreadcrumbClick = (id, index) => {
    setCurrentFolderId(id);
    setBreadcrumbs((prev) => prev.slice(0, index + 1));
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    setCreateFolderError("");
    try {
      await dispatch(create_folder({
        name: newFolderName,
        parent_id: currentFolderId,
        is_personal: isPersonal
      })).unwrap();
      setNewFolderName("");
      setIsCreateFolderOpen(false);
    } catch (err) {
      // Extract the validation message from the backend 400 response
      const msg = err?.detail ||
        (typeof err === "string" ? err : null) ||
        "Failed to create folder. Please try again.";
      setCreateFolderError(msg);
    }
  };

  const handleOpenFolderMenu = (event, folder) => {
    event.stopPropagation();
    setFolderMenuAnchor(event.currentTarget);
    setActiveFolder(folder);
  };

  const handleCloseFolderMenu = () => {
    setFolderMenuAnchor(null);
  };

  const handleRenameClick = () => {
    setRenameValue(activeFolder.name);
    setIsRenameDialogOpen(true);
    handleCloseFolderMenu();
  };

  const handleRenameSubmit = async () => {
    if (!renameValue.trim()) return;
    setRenameFolderError("");
    try {
      await dispatch(update_folder({ folder_id: activeFolder.id, name: renameValue })).unwrap();
      setIsRenameDialogOpen(false);
    } catch (err) {
      // Extract the validation message from the backend 400 response
      const msg = err?.detail ||
        (typeof err === "string" ? err : null) ||
        "Failed to rename folder. Please try again.";
      setRenameFolderError(msg);
    }
  };

  const handleDeleteFolder = (folder) => {
    setDeleteTarget(folder);
    setIsDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await dispatch(delete_folder(deleteTarget.id)).unwrap();
      setIsDeleteConfirmOpen(false);
      setDeleteTarget(null);
      // Refresh both folders and files for current location
      dispatch(get_folders({ parent_id: currentFolderId, is_personal: isPersonal }));
      dispatch(get_brand_library({ search: search.trim(), company_id: selected_company, is_personal: isPersonal, folder_id: currentFolderId }));
    } catch (err) {
      // Backend returns 400 when folder is not empty
      const msg = err?.detail ||
        (typeof err === "string" ? err : null) ||
        "This folder cannot be deleted because it contains files or subfolders.";
      setIsDeleteConfirmOpen(false);
      setFolderNotEmptyError(msg);
    }
  };


  const handleDeleteFile = (item) => {
    setFileDeleteTarget(item);
    setIsFileDeleteConfirmOpen(true);
  };

  const handleFileDeleteConfirm = async () => {
    if (!fileDeleteTarget) return;
    try {
      await dispatch(remove_brand(fileDeleteTarget.id)).unwrap();
      setIsFileDeleteConfirmOpen(false);
      setFileDeleteTarget(null);
    } catch (err) {
      console.error("Failed to delete file", err);
      setIsFileDeleteConfirmOpen(false);
    }
  };

  // ── existing handleToggleLibrary below ──
  const handleToggleLibrary = () => {
    const nextStage = isPersonal ? "brand_library" : "my_library";
    dispatch(setStageTab(nextStage));
    setCurrentFolderId(null);
    setBreadcrumbs([{ id: null, name: "Home" }]);
    // StageList already navigates to /brand-library, but this keeps it safe if user toggles here
    navigate("/brand-library");
  };


  const handleDownload = async (item) => {
    try {
      const fileKey = item.file_path?.replace(/^\/+/, "");
      const fileName = item.file_path?.split("/").pop() || "download";
      const directUrl = getFullUrl(item.file_path);

      if (item.job_id && fileKey) {
        const action = await dispatch(
          download_file({
            job_id: item.job_id,
            file_key: fileKey,
            d_type: "attachment",
          })
        );

        const finalUrl = action.payload?.data?.url || action.payload?.url;
        if (finalUrl) {
          const link = document.createElement("a");
          link.href = finalUrl;
          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          return;
        }
      }

      if (directUrl) {
        const link = document.createElement("a");
        link.href = directUrl;
        link.download = fileName;
        link.target = "_blank";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err) {
      console.error("Download failed:", err);
    }
  };

  const renderThumbnail = (item) => {
    const url = getFullUrl(item.file_path);

    if (!url) return <DescriptionIcon sx={{ fontSize: 80, color: "#bdbdbd" }} />;

    if (isImage(url)) {
      if (brokenImages[url]) return <DescriptionIcon sx={{ fontSize: 80, color: "#bdbdbd" }} />;
      return (
        <img
          src={url}
          alt={item.name || "Brand Asset"}
          style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
          onError={() => setBrokenImages((prev) => ({ ...prev, [url]: true }))}
        />
      );
    }

    if (isPdf(url)) return <PdfThumbnail url={url} />;

    return <DescriptionIcon sx={{ fontSize: 80, color: "#bdbdbd" }} />;
  };

  return (
    <Box sx={{ p: 0, bgcolor: "transparent", minHeight: "85vh", pb: 4 }}>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          display: "flex",
          alignItems: "center",
          borderBottom: "1px solid #eee",
          bgcolor: "#fff",
          borderRadius: "10px",
        }}
      >
        <Box sx={{ minWidth: 200, display: "flex", alignItems: "center" }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mr: 2 }}>
            Library : {isPersonal ? "My Library" : "Brand Library"}
          </Typography>
        </Box>

        <Box sx={{ flexGrow: 1, px: 4 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Library Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleKeyDown}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {search && (
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSearch("");
                        setCurrentFolderId(null);
                        setBreadcrumbs([{ id: null, name: "Home" }]);

                        dispatch(get_folders({
                          parent_id: null,
                          is_personal: isPersonal,
                        }));

                        dispatch(get_brand_library({
                          search: "",
                          company_id: selected_company,
                          is_personal: isPersonal,
                          folder_id: null
                        }));
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  )}
                  <IconButton onClick={handleSearch}>
                    <SearchIcon />
                  </IconButton>

                </InputAdornment>
              ),
              sx: { borderRadius: "8px", bgcolor: "#fcfcfc" },
            }}
          />
        </Box>

        <Button
          variant="contained"
          startIcon={<CreateNewFolderIcon />}
          onClick={() => setIsCreateFolderOpen(true)}
          sx={{
            mr: 2,
            bgcolor: "#fff",
            color: "#333",
            border: "1px solid #ddd",
            boxShadow: "none",
            "&:hover": { bgcolor: "#f5f5f5", boxShadow: "none", borderColor: "#ccc" },
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          New Folder
        </Button>


        <Stack direction="row" spacing={1} alignItems="center">
          {/* <IconButton
            size="small"
            sx={{ border: "1px solid #ddd", borderRadius: "4px" }}
            disabled={page === 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            <ChevronLeftIcon />
          </IconButton>

          <Box
            sx={{
              width: 30,
              height: 30,
              bgcolor: "#ff6100",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "4px",
              fontSize: "14px",
              fontWeight: "bold",
            }}
          >
            {page}
          </Box>

          <IconButton
            size="small"
            sx={{ border: "1px solid #ddd", borderRadius: "4px" }}
            disabled={page >= pageCount}
            onClick={() => setPage((p) => Math.min(pageCount || 1, p + 1))}
          >
            <ChevronRightIcon />
          </IconButton> */}
        </Stack>
      </Paper>

      {/* Breadcrumbs */}
      <Box sx={{ mb: 3, display: "flex", alignItems: "center" }}>
        {currentFolderId && (
          <IconButton
            size="small"
            onClick={() => handleBreadcrumbClick(breadcrumbs[breadcrumbs.length - 2]?.id, breadcrumbs.length - 2)}
            sx={{ mr: 1, bgcolor: "#fff", border: "1px solid #eee" }}
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        )}
        <Breadcrumbs
          separator={<ChevronRightIcon sx={{ fontSize: 16 }} />}
          sx={{
            "& .MuiBreadcrumbs-li": { fontSize: "0.95rem", fontWeight: 500 },
          }}
        >
          {breadcrumbs.map((bc, index) => (
            <Link
              key={bc.id || "root"}
              underline="hover"
              color={index === breadcrumbs.length - 1 ? "text.primary" : "inherit"}
              sx={{
                cursor: "pointer",
                fontWeight: index === breadcrumbs.length - 1 ? 700 : 500,
                display: "flex",
                alignItems: "center",
              }}
              onClick={() => handleBreadcrumbClick(bc.id, index)}
            >
              {index === 0 && <HomeIcon sx={{ mr: 0.5, fontSize: 18 }} />}
              {bc.name}
            </Link>
          ))}
        </Breadcrumbs>
      </Box>


      {loading || folderLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 10 }}>
          <CircularProgress sx={{ color: "#ff6100" }} />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {/* Render Folders First */}
          {folders && folders.map((folder) => (
            <Grid item xs={6} sm={4} md={3} lg={2} key={folder.id}>
              <Box
                onClick={() => handleFolderClick(folder)}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  py: 3,
                  borderRadius: "10px",
                  transition: "all .15s ease",
                  "&:hover": {
                    bgcolor: "rgba(0,0,0,0.04)"
                  }
                }}
              >
                {/* Folder Icon */}
                <FolderIcon
                  sx={{
                    fontSize: 150,
                    color: "#f4c430"
                  }}
                />

                {/* Folder Name */}
                <Typography
                  sx={{
                    mt: 1,
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#555",
                    textAlign: "center"
                  }}
                >
                  {folder.name}
                </Typography>

                {/* Action icons below the folder name */}
                <Box
                  onClick={(e) => e.stopPropagation()}
                  sx={{ display: "flex", gap: 0.5, mt: 0.5 }}
                >
                  <Tooltip title="Rename">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveFolder(folder);
                        setRenameValue(folder.name);
                        setIsRenameDialogOpen(true);
                      }}
                      sx={{
                        color: "#1976d2",
                        "&:hover": { bgcolor: "rgba(25,118,210,0.08)" }
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteFolder(folder);
                      }}
                      sx={{
                        color: "#d32f2f",
                        "&:hover": { bgcolor: "rgba(211,47,47,0.08)" }
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Grid>
          ))}


          {/* Render Files */}
          {paginatedItems && paginatedItems.length > 0 ? (

            paginatedItems.map((item) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={item.id}>
                <Card
                  sx={{
                    borderRadius: "12px",
                    overflow: "hidden",
                    boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
                    position: "relative",
                    border: "1px solid #efefef",
                    "&:hover": { boxShadow: "0 4px 20px rgba(0,0,0,0.1)" },
                  }}
                >
                  <Box
                    sx={{
                      height: 240,
                      position: "relative",
                      backgroundColor: "#fff",
                      backgroundImage:
                        "linear-gradient(45deg, #f5f5f5 25%, transparent 25%), linear-gradient(-45deg, #f5f5f5 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #f5f5f5 75%), linear-gradient(-45deg, transparent 75%, #f5f5f5 75%)",
                      backgroundSize: "20px 20px",
                      backgroundPosition: "0 0, 0 10px, 10px -10px, -10px 0px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      p: 2,
                    }}
                  >
                    {renderThumbnail(item)}

                    <Box
                      onClick={() => {
                        const url = selected_process_tab
                          ? `/create-job?process_id=${selected_process_tab}`
                          : "/create-job";
                        navigate(url);
                      }}
                      sx={{
                        position: "absolute",
                        top: 15,
                        left: 15,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        cursor: "pointer",
                        bgcolor: "rgba(255,255,255,0.7)",
                        px: 1,
                        py: 0.5,
                        borderRadius: "4px",
                        "&:hover": { bgcolor: "#fff" },
                      }}
                    >
                      {/* <LibraryBooksOutlinedIcon sx={{ fontSize: 18, color: "#333" }} /> */}
                      {/* <Typography
                        variant="caption"
                        sx={{ fontSize: "14px", fontWeight: 600, color: "#333" }}
                      >
                        Start a job from this
                      </Typography> */}
                    </Box>

                    <IconButton
                      onClick={() => handleDownload(item)}
                      sx={{
                        position: "absolute",
                        top: 10,
                        right: 40,
                        bgcolor: "transparent",
                        color: "#333",
                        p: 0.5,
                      }}
                      size="small"
                    >
                      <FileDownloadOutlinedIcon fontSize="small" />
                    </IconButton>

                    {/* Delete File */}
                    <Tooltip title="Delete file">
                      <IconButton
                        onClick={() => handleDeleteFile(item)}
                        sx={{
                          position: "absolute",
                          top: 10,
                          right: 8,
                          bgcolor: "transparent",
                          color: "#d32f2f",
                          p: 0.5,
                          "&:hover": { bgcolor: "rgba(211,47,47,0.08)" },
                        }}
                        size="small"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  <Box
                    sx={{
                      p: 2,
                      bgcolor: "#fff",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                    }}
                  >
                    <Box sx={{ flex: 1, pr: 1, display: "flex", flexDirection: "column", gap: 0.5 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 700,
                          fontSize: "12px",
                          lineHeight: 1.2,
                          color: "#222",
                        }}
                      >
                        {item.name && item.name !== "-" ? item.name : getFileName(item.file_path)}
                      </Typography>

                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {item.search_param
                          ?.split(",")
                          .map((t) => t.trim())
                          .filter((t) => t && t !== "-")
                          .map((tag, i) => (
                            <Chip
                              key={i}
                              label={tag}
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: "12px",
                                borderRadius: "6px",
                                bgcolor: "#f5f5f5",
                                border: "1px solid #e0e0e0",
                                color: "#666",
                                fontWeight: 600,
                                "& .MuiChip-label": { px: 1 },
                              }}
                            />
                          ))}
                      </Box>

                      {item.process_id && company_process && (
                        <Box>
                          <Chip
                            label={company_process.find((p) => p.id === item.process_id)?.name}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: "12px",
                              borderRadius: "6px",
                              bgcolor: "#f5f5f5",
                              border: "1px solid #e0e0e0",
                              color: "#666",
                              fontWeight: 600,
                              "& .MuiChip-label": { px: 1 },
                            }}
                          />
                        </Box>
                      )}
                      {/* {item.company_id && user_company && (
                        <Box>
                          <Chip
                            label={user_company.find((p) => p.id === item.company_id)?.company_name}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: "12px",
                              borderRadius: "6px",
                              bgcolor: "#f5f5f5",
                              border: "1px solid #e0e0e0",
                              color: "#666",
                              fontWeight: 600,
                              "& .MuiChip-label": { px: 1 },
                            }}
                          />
                        </Box>
                      )} */}
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-end",
                        minWidth: "90px",
                        flexShrink: 0,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 600, color: "#bdbdbd", fontSize: "0.75rem", mb: 0.5 }}
                      >
                        {getExtension(item.file_path)}
                      </Typography>

                      <Typography variant="caption" sx={{ fontSize: "12px", color: "#757575" }}>
                        {formatDateTime(item.created_date)}
                      </Typography>

                      {/* <Typography variant="caption" sx={{ fontSize: "12px", color: "#9e9e9e", mt: 0.5 }}>
                        {item.id ? `${item.id}JQP8` : ""}
                      </Typography> */}
                    </Box>
                  </Box>
                </Card>
              </Grid>
            ))
          ) : (!folders || folders.length === 0) ? (
            <Grid item xs={12}>
              <Box sx={{ p: 10, textAlign: "center", bgcolor: "#fff", borderRadius: "12px" }}>
                <Typography variant="h6" color="text.secondary">
                  {search
                    ? "No items found matching your search."
                    : `No folders or items found in your ${isPersonal ? "personal library" : "brand library"}.`
                  }
                </Typography>
              </Box>
            </Grid>
          ) : null}
        </Grid>
      )}

      {/* Create Folder Dialog */}
      <Dialog open={isCreateFolderOpen} onClose={() => { setIsCreateFolderOpen(false); setCreateFolderError(""); setNewFolderName(""); }}>
        <DialogTitle>Create New Folder</DialogTitle>
        <DialogContent sx={{ minWidth: 350, pt: 1 }}>
          <TextField
            autoFocus
            fullWidth
            label="Folder Name"
            variant="outlined"
            size="small"
            value={newFolderName}
            onChange={(e) => { setNewFolderName(e.target.value); setCreateFolderError(""); }}
            onKeyDown={(e) => e.key === "Enter" && handleCreateFolder()}
            error={!!createFolderError}
            helperText={createFolderError}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setIsCreateFolderOpen(false); setCreateFolderError(""); setNewFolderName(""); }}>Cancel</Button>
          <Button
            onClick={handleCreateFolder}
            variant="contained"
            disabled={!newFolderName.trim()}
            sx={{ bgcolor: "#ff6100", "&:hover": { bgcolor: "#e65700" } }}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rename Folder Dialog */}
      <Dialog open={isRenameDialogOpen} onClose={() => { setIsRenameDialogOpen(false); setRenameFolderError(""); }} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Rename Folder</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <TextField
            autoFocus
            fullWidth
            label="Folder Name"
            variant="outlined"
            size="small"
            value={renameValue}
            onChange={(e) => { setRenameValue(e.target.value); setRenameFolderError(""); }}
            onKeyDown={(e) => e.key === "Enter" && handleRenameSubmit()}
            error={!!renameFolderError}
            helperText={renameFolderError}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setIsRenameDialogOpen(false); setRenameFolderError(""); }}>Cancel</Button>
          <Button
            onClick={handleRenameSubmit}
            variant="contained"
            disabled={!renameValue.trim() || renameValue.trim() === activeFolder?.name}
            sx={{ bgcolor: "#ff6100", "&:hover": { bgcolor: "#e65700" } }}
          >
            Rename
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Folder Confirmation Dialog — only shown for empty folders */}
      <Dialog open={isDeleteConfirmOpen} onClose={() => setIsDeleteConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete Folder</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>"{deleteTarget?.name}"</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setIsDeleteConfirmOpen(false); setDeleteTarget(null); }}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Folder Not Empty — error dialog */}
      <Dialog open={!!folderNotEmptyError} onClose={() => setFolderNotEmptyError("")} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: "#d32f2f" }}>Cannot Delete Folder</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ bgcolor: "#fff3e0", p: 1.5, borderRadius: 1, border: "1px solid #ffe0b2" }}>
            🚫 {folderNotEmptyError}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setFolderNotEmptyError("")} variant="contained" sx={{ bgcolor: "#ff6100", "&:hover": { bgcolor: "#e65700" } }}>
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete File Confirmation Dialog */}
      <Dialog open={isFileDeleteConfirmOpen} onClose={() => setIsFileDeleteConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete File</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>"{fileDeleteTarget?.name || "this file"}"</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setIsFileDeleteConfirmOpen(false); setFileDeleteTarget(null); }}>Cancel</Button>
          <Button
            onClick={handleFileDeleteConfirm}
            variant="contained"
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>


  );
};

export default BrandLibraryPage;
